import { useCallback, useEffect, useState } from "react";

const TOKEN_KEY = "ihcc-admin-token";
const USER_KEY = "ihcc-admin-user";
const AUTH_EVENT = "ihcc-auth-change";

function readAuth() {
  const token =
    localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
  const storedUser =
    localStorage.getItem(USER_KEY) || sessionStorage.getItem(USER_KEY);

  let adminUser = null;
  if (storedUser) {
    try {
      adminUser = JSON.parse(storedUser);
    } catch {
      adminUser = null;
    }
  }

  return { isAdmin: Boolean(token), adminUser };
}

function clearStorage() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
}

/**
 * Reads the admin session from local/session storage and keeps every
 * component that uses the hook in sync. Any component can call login/logout
 * and the navbar (which uses its own instance of this hook) updates instantly
 * thanks to the broadcasted "ihcc-auth-change" event.
 */
export function useAdminAuth() {
  const [{ isAdmin, adminUser }, setState] = useState(() => {
    // Guard for non-browser environments.
    if (typeof window === "undefined") {
      return { isAdmin: false, adminUser: null };
    }
    return readAuth();
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setState(readAuth());
    setIsLoading(false);

    const sync = () => setState(readAuth());

    // Same-tab updates (login/logout in another component)
    window.addEventListener(AUTH_EVENT, sync);
    // Cross-tab updates
    window.addEventListener("storage", sync);

    return () => {
      window.removeEventListener(AUTH_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const login = useCallback((user) => {
    const token = user?.token;
    if (!token) return;

    const remember = Boolean(user?.remember);
    const storage = remember ? localStorage : sessionStorage;

    clearStorage();
    storage.setItem(TOKEN_KEY, token);
    storage.setItem(USER_KEY, JSON.stringify({ email: user.email, remember }));

    setState({ isAdmin: true, adminUser: { email: user.email, remember } });
    window.dispatchEvent(new Event(AUTH_EVENT));
  }, []);

  const logout = useCallback(() => {
    clearStorage();
    setState({ isAdmin: false, adminUser: null });
    window.dispatchEvent(new Event(AUTH_EVENT));
  }, []);

  return { isAdmin, adminUser, isLoading, login, logout };
}
